import { Button, Table } from 'antd';
import React, { FC, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { SystemActions } from '../actions';
import { CustomModal } from '../components/Common';
import { useLookupData } from '../hooks';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h3 {
    font-weight: normal !important;
    text-align: center;
    margin: 30px 0 15px 0;
  }

  h4 {
    margin-top: 0 !important;
    font-weight: bold;
    text-align: left;
    margin-bottom: 20px;
  }

  table {
    tr {
      td:first-child {
        padding-left: 0 !important;
      }

      :hover {
        td {
          background: transparent !important;
        }
      }

      &:last-child {
        td {
          border-bottom: none;
        }
      }
    }
  }
`;

interface AboutModalProps {
  visible: boolean;
  onClose: () => void;
}

const detailsTableColumn = [
  {
    dataIndex: 'label',
    key: 'label',
    width: 150,
  },
  {
    dataIndex: 'details',
    key: 'details',
  },
];

const mapStateToProps = ({
  system: { releaseDetails, isLoadingReleaseDetails },
}: STATES.AppState) => ({
  releaseDetails,
  isLoadingReleaseDetails,
});

const AboutModal: FC<AboutModalProps> = ({ visible, onClose }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { releaseNote, releaseVersion, isLoadingLookupData } = useLookupData();
  const { releaseDetails, isLoadingReleaseDetails } = useSelector(
    mapStateToProps
  );

  useEffect(() => {
    if (!releaseDetails) {
      dispatch(SystemActions.getReleaseDetails());
    }
  }, [dispatch, releaseDetails]);

  const data = useMemo(() => {
    return [
      {
        label: intl.formatMessage({ id: 'AboutModal.table.versionNumber' }),
        details: releaseDetails && releaseDetails.releaseVersion,
      },
      {
        label: intl.formatMessage({ id: 'AboutModal.table.versionDetails' }),
        details: releaseNote,
      },
      {
        label: intl.formatMessage({ id: 'AboutModal.table.releasedDate' }),
        details: releaseDetails && releaseDetails.releaseDate,
      },
      {
        label: intl.formatMessage({ id: 'AboutModal.table.releaseVersion' }),
        details: process.env.REACT_APP_VERSION ?? releaseVersion,
      },
    ];
  }, [intl, releaseDetails, releaseVersion, releaseNote]);

  return (
    <>
      <CustomModal
        visible={visible}
        closable
        getContainer={document.body}
        showLoading
        isContentLoading={isLoadingReleaseDetails || isLoadingLookupData}
        title={<FormattedMessage id="AboutModal.heading" />}
        content={
          <Content>
            <Table
              showHeader={false}
              pagination={false}
              columns={detailsTableColumn}
              dataSource={data}
              rowKey="label"
            />
          </Content>
        }
        footer={
          <Button
            data-testid="about-modal-ok-btn"
            type="primary"
            shape="round"
            onClick={onClose}
          >
            <FormattedMessage id="AboutModal.table.ok" />
          </Button>
        }
        destroyOnClose
        onCancel={onClose}
      />
    </>
  );
};

export default AboutModal;
